import { useState, useEffect, useCallback } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { useAuth } from '../../../contexts/AuthContext';
import { apiGet, apiPost } from '../../../lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ContestData {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  rulesUrl: string | null;
  contestType: string;
  status: string;
  startDate: string;
  endDate: string;
  config: Record<string, unknown>;
  entryConfig: Record<string, unknown>;
  winValidation: string;
}

interface Entitlement {
  playsEarned: number;
  playsUsed: number;
  playsRemaining: number;
}

interface PlayResult {
  outcome: 'win' | 'lose' | 'consolation';
  prize?: { id: string; prizeType: string; prizeValue: Record<string, unknown> };
  consolationValue?: { points: number };
  attemptId: string;
}

interface LeaderboardEntry {
  rank: number;
  customerId: string;
  score: number;
  firstName?: string;
  lastName?: string;
}

interface LeaderboardResponse {
  entries: LeaderboardEntry[];
  customerEntry: { rank: number; score: number } | null;
}

interface LoyaltyContestBlockProps {
  contestId?: string;
  roundId?: string;
  programId?: string;
  title?: string;
  maxLeaderboardEntries?: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCountdown(endDate: string): string {
  const diff = new Date(endDate).getTime() - Date.now();
  if (diff <= 0) return 'ENDED';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} left`;
  if (hours > 0) return `${hours}h left`;
  return 'Ending soon';
}

function formatPrize(prize: PlayResult['prize']): string {
  if (!prize) return '';
  const v = prize.prizeValue;
  if (prize.prizeType === 'points' && typeof v.points === 'number') {
    return `${(v.points as number).toLocaleString()} Points`;
  }
  if (prize.prizeType === 'coupon' && v.code) return `Coupon: ${v.code}`;
  if (prize.prizeType === 'voucher' && typeof v.amount === 'number') {
    return `€${v.amount} Voucher`;
  }
  return 'Prize';
}

// ─── LoginCta ─────────────────────────────────────────────────────────────────

function LoginCta({ contestType }: { contestType?: string }) {
  const emoji = contestType === 'draw' ? '🎟️' : contestType === 'leaderboard' ? '🏆' : '🎮';
  const label = contestType === 'draw' ? 'contest' : contestType === 'leaderboard' ? 'leaderboard' : 'game';

  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">{emoji}</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Login to join this {label}
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to participate and earn rewards.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

// ─── InstantWinView ───────────────────────────────────────────────────────────

function InstantWinView({
  contest,
  roundId,
}: {
  contest: ContestData;
  roundId: string;
}) {
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [entLoading, setEntLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState<PlayResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const gameUI = (contest.config.gameUI as string) ?? 'none';

  const fetchEntitlement = useCallback(() => {
    setEntLoading(true);
    apiGet<Entitlement>(`/customer/loyalty/contests/${contest.id}/entitlement?roundId=${roundId}`)
      .then(setEntitlement)
      .catch(() => setEntitlement({ playsEarned: 0, playsUsed: 0, playsRemaining: 0 }))
      .finally(() => setEntLoading(false));
  }, [contest.id, roundId]);

  useEffect(() => {
    fetchEntitlement();
  }, [fetchEntitlement]);

  const isUnlimited = entitlement?.playsRemaining === -1;

  const handlePlay = async () => {
    if (playing || (!isUnlimited && (!entitlement || entitlement.playsRemaining <= 0))) return;
    setPlaying(true);
    setResult(null);
    setError(null);
    try {
      const res = await apiPost<PlayResult>(`/customer/loyalty/contests/${contest.id}/play`, {
        roundId,
        gameType: gameUI !== 'none' ? gameUI : undefined,
      });
      setResult(res);
      if (!isUnlimited) fetchEntitlement();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg.includes('No plays') ? 'No plays remaining.' : 'Something went wrong. Try again.');
    } finally {
      setPlaying(false);
    }
  };

  const playsLeft = isUnlimited ? Infinity : (entitlement?.playsRemaining ?? 0);

  return (
    <div className="loyalty-card overflow-hidden">
      {/* Header image */}
      {contest.imageUrl && (
        <div className="relative h-40 bg-[var(--color-surface)]">
          <img src={contest.imageUrl} alt={contest.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <span className="absolute top-3 right-3 text-xs font-bold px-2 py-1 rounded bg-[var(--color-primary)] text-white">
            {formatCountdown(contest.endDate)}
          </span>
        </div>
      )}

      <div className="p-6">
        <h2 className="loyalty-heading text-xl text-[var(--color-text)] mb-1">{contest.title}</h2>
        {contest.description && (
          <p className="text-sm text-[var(--color-text-muted)] mb-4">{contest.description}</p>
        )}

        {/* Plays counter (hidden for unlimited contests) */}
        {!isUnlimited && (
          <div className="flex items-center gap-3 mb-6">
            {entLoading ? (
              <div className="h-8 w-32 bg-[var(--color-surface-border)] rounded animate-pulse" />
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-[var(--color-primary)]">{playsLeft === Infinity ? '∞' : playsLeft}</span>
                <span className="text-sm text-[var(--color-text-muted)]">
                  plays remaining
                </span>
              </div>
            )}
            {entitlement && (
              <span className="ml-auto text-xs text-[var(--color-text-muted)]">
                {entitlement.playsUsed}/{entitlement.playsEarned} used
              </span>
            )}
          </div>
        )}

        {/* Result overlay */}
        {result && (
          <div
            className={`mb-6 rounded-xl p-5 text-center border ${
              result.outcome === 'win'
                ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800'
                : result.outcome === 'consolation'
                ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-800'
                : 'bg-[var(--color-surface)] border-[var(--color-surface-border)]'
            }`}
          >
            {result.outcome === 'win' ? (
              <>
                <div className="text-4xl mb-2">🏆</div>
                <p className="font-bold text-green-700 dark:text-green-400 text-lg mb-1">You Won!</p>
                {result.prize && (
                  <p className="text-sm text-green-600 dark:text-green-500">{formatPrize(result.prize)}</p>
                )}
              </>
            ) : result.outcome === 'consolation' ? (
              <>
                <div className="text-4xl mb-2">⭐</div>
                <p className="font-bold text-yellow-700 dark:text-yellow-400 text-lg mb-1">
                  Consolation Prize!
                </p>
                {result.consolationValue && (
                  <p className="text-sm text-yellow-600 dark:text-yellow-500">
                    +{result.consolationValue.points.toLocaleString()} Points
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="text-4xl mb-2">😔</div>
                <p className="font-bold text-[var(--color-text)] text-lg mb-1">Not this time!</p>
                <p className="text-sm text-[var(--color-text-muted)]">Better luck next play.</p>
              </>
            )}
            <button
              onClick={() => setResult(null)}
              className="mt-3 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm dark:bg-red-950/30 dark:border-red-800 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Play CTA */}
        <button
          onClick={handlePlay}
          disabled={playing || (!isUnlimited && playsLeft <= 0) || entLoading}
          className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${
            (isUnlimited || playsLeft > 0) && !entLoading
              ? 'bg-[var(--color-primary)] text-white hover:opacity-90 active:scale-95'
              : 'bg-[var(--color-surface-border)] text-[var(--color-text-muted)] cursor-not-allowed'
          }`}
        >
          {playing ? (
            <span className="flex items-center justify-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Playing…
            </span>
          ) : !isUnlimited && playsLeft <= 0 ? (
            'No Plays Remaining'
          ) : gameUI === 'wheel' ? (
            'Spin the Wheel'
          ) : gameUI === 'scratch' ? (
            'Scratch & Win'
          ) : gameUI === 'quiz' ? (
            'Answer Now'
          ) : gameUI === 'memory' ? (
            'Play Memory'
          ) : (
            'Play Now'
          )}
        </button>

        {contest.rulesUrl && (
          <div className="mt-3 text-center">
            <a
              href={contest.rulesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] underline"
            >
              Contest Rules
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DrawView ─────────────────────────────────────────────────────────────────

function DrawView({
  contest,
  roundId,
}: {
  contest: ContestData;
  roundId: string;
}) {
  const [entered, setEntered] = useState(false);
  const [entryChecked, setEntryChecked] = useState(false);
  const [entering, setEntering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefetch entry status so returning users see the confirmed state immediately
  useEffect(() => {
    let cancelled = false;
    apiGet<{ entered: boolean }>(`/customer/loyalty/contests/${contest.id}/entered?roundId=${roundId}`)
      .then((res) => { if (!cancelled) { setEntered(res.entered); setEntryChecked(true); } })
      .catch(() => { if (!cancelled) setEntryChecked(true); });
    return () => { cancelled = true; };
  }, [contest.id, roundId]);

  const handleEnter = async () => {
    if (entering || entered) return;
    setEntering(true);
    setError(null);
    try {
      await apiPost(`/customer/loyalty/contests/${contest.id}/enter`, { roundId });
      setEntered(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('already') || msg.includes('409')) {
        setEntered(true);
      } else {
        setError(msg.includes('requirement') ? msg : 'Could not enter contest. Please try again.');
      }
    } finally {
      setEntering(false);
    }
  };

  const entryConfig = contest.entryConfig as {
    type?: string;
    requirements?: { minTier?: string; minPoints?: number; badgeCode?: string };
  };

  const requirements = entryConfig.requirements;
  const hasRequirements =
    entryConfig.type === 'free_entry' &&
    requirements &&
    (requirements.minTier || requirements.minPoints || requirements.badgeCode);

  if (!entryChecked) {
    return (
      <div className="loyalty-card p-6 animate-pulse">
        <div className="h-5 w-48 bg-[var(--color-surface-border)] rounded mb-3" />
        <div className="h-4 w-64 bg-[var(--color-surface-border)] rounded mb-6" />
        <div className="h-10 w-full bg-[var(--color-surface-border)] rounded" />
      </div>
    );
  }

  return (
    <div className="loyalty-card overflow-hidden">
      {contest.imageUrl && (
        <div className="relative h-40 bg-[var(--color-surface)]">
          <img src={contest.imageUrl} alt={contest.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <span className="absolute bottom-3 left-4 text-sm font-bold text-white">
            {formatCountdown(contest.endDate)}
          </span>
        </div>
      )}

      <div className="p-6">
        <h2 className="loyalty-heading text-xl text-[var(--color-text)] mb-2">{contest.title}</h2>
        {contest.description && (
          <p className="text-sm text-[var(--color-text-muted)] mb-4">{contest.description}</p>
        )}

        {/* Requirements */}
        {hasRequirements && (
          <div className="mb-4 p-3 rounded-xl bg-[var(--color-surface)] border border-[var(--color-surface-border)]">
            <p className="text-xs font-bold uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
              Entry Requirements
            </p>
            <ul className="space-y-1">
              {requirements?.minTier && (
                <li className="text-sm text-[var(--color-text)] flex items-center gap-1.5">
                  <span className="text-[var(--color-primary)]">✓</span>
                  Minimum tier: <span className="font-semibold capitalize">{requirements.minTier}</span>
                </li>
              )}
              {requirements?.minPoints && (
                <li className="text-sm text-[var(--color-text)] flex items-center gap-1.5">
                  <span className="text-[var(--color-primary)]">✓</span>
                  Minimum points: <span className="font-semibold">{requirements.minPoints.toLocaleString()}</span>
                </li>
              )}
              {requirements?.badgeCode && (
                <li className="text-sm text-[var(--color-text)] flex items-center gap-1.5">
                  <span className="text-[var(--color-primary)]">✓</span>
                  Required badge: <span className="font-semibold capitalize">{requirements.badgeCode.replace(/_/g, ' ')}</span>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Entry status */}
        {entered ? (
          <div className="mb-4 rounded-xl p-5 text-center bg-green-50 border border-green-200 dark:bg-green-950/30 dark:border-green-800">
            <div className="text-3xl mb-2">🎟️</div>
            <p className="font-bold text-green-700 dark:text-green-400 text-base mb-1">You're Entered!</p>
            <p className="text-sm text-green-600 dark:text-green-500">
              Good luck in the draw. Winners will be announced soon.
            </p>
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm dark:bg-red-950/30 dark:border-red-800 dark:text-red-400">
                {error}
              </div>
            )}
            <button
              onClick={handleEnter}
              disabled={entering}
              className="w-full py-3 rounded-xl font-bold text-sm bg-[var(--color-primary)] text-white hover:opacity-90 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {entering ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Entering…
                </span>
              ) : (
                'Enter Draw'
              )}
            </button>
          </>
        )}

        {contest.rulesUrl && (
          <div className="mt-3 text-center">
            <a
              href={contest.rulesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] underline"
            >
              Contest Rules
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── LeaderboardView ──────────────────────────────────────────────────────────

function LeaderboardView({
  contest,
  roundId,
  maxEntries,
}: {
  contest: ContestData;
  roundId: string;
  maxEntries: number;
}) {
  const { user } = useAuth();
  const [board, setBoard] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet<LeaderboardResponse>(
      `/customer/loyalty/leaderboard/${roundId}?limit=${maxEntries}`,
    )
      .then((res) => { if (!cancelled) setBoard(res); })
      .catch(() => { if (!cancelled) setBoard(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [roundId, maxEntries]);

  const customerId = user?.sub;

  return (
    <div className="loyalty-card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="loyalty-heading text-xl text-[var(--color-text)]">{contest.title}</h2>
        {!contest.imageUrl && (
          <span className="text-xs text-[var(--color-text-muted)]">{formatCountdown(contest.endDate)}</span>
        )}
      </div>
      {contest.description && (
        <p className="text-sm text-[var(--color-text-muted)] mb-4">{contest.description}</p>
      )}

      {/* Customer's own rank */}
      {board?.customerEntry && (
        <div className="mb-4 p-3 rounded-xl bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/30 flex items-center justify-between">
          <span className="text-sm font-bold text-[var(--color-primary)]">Your Position</span>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[var(--color-text)]">
              Score: <strong>{board.customerEntry.score.toLocaleString()}</strong>
            </span>
            <span className="font-bold text-[var(--color-primary)]">
              #{board.customerEntry.rank}
            </span>
          </div>
        </div>
      )}

      {/* Leaderboard table */}
      {loading ? (
        <div className="space-y-2 animate-pulse">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-10 bg-[var(--color-surface-border)] rounded" />
          ))}
        </div>
      ) : !board || board.entries.length === 0 ? (
        <div className="py-8 text-center text-sm text-[var(--color-text-muted)]">
          No entries yet. Be the first to play!
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[var(--color-text-muted)] text-xs uppercase tracking-wider border-b border-[var(--color-surface-border)]">
                <th className="text-left py-2 pr-2 w-10">Rank</th>
                <th className="text-left py-2 pr-2">Player</th>
                <th className="text-right py-2">Score</th>
              </tr>
            </thead>
            <tbody>
              {board.entries.map((entry) => {
                const isMe = customerId && entry.customerId === customerId;
                const medal =
                  entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : null;
                return (
                  <tr
                    key={entry.customerId}
                    className={`border-b border-[var(--color-surface-border)] last:border-0 transition-colors ${
                      isMe ? 'bg-[var(--color-primary)]/5' : ''
                    }`}
                  >
                    <td className="py-2.5 pr-2 font-bold text-[var(--color-text-muted)]">
                      {medal ?? `#${entry.rank}`}
                    </td>
                    <td className="py-2.5 pr-2">
                      <span className={`${isMe ? 'font-bold text-[var(--color-primary)]' : 'text-[var(--color-text)]'}`}>
                        {entry.firstName && entry.lastName
                          ? `${entry.firstName} ${entry.lastName[0]}.`
                          : `Player ${entry.rank}`}
                        {isMe && (
                          <span className="ml-1.5 text-[10px] bg-[var(--color-primary)] text-white rounded px-1 py-0.5 font-bold uppercase">
                            You
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-2.5 text-right font-bold text-[var(--color-text)]">
                      {entry.score.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {contest.rulesUrl && (
        <div className="mt-4 text-center">
          <a
            href={contest.rulesUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] underline"
          >
            Contest Rules
          </a>
        </div>
      )}
    </div>
  );
}

// ─── Main Block ───────────────────────────────────────────────────────────────

export function LoyaltyContestBlock({ props }: { props: LoyaltyContestBlockProps }) {
  const { data: loyaltyData, loading: loyaltyLoading } = useLoyalty();
  const { contestId, roundId, programId, maxLeaderboardEntries = 10 } = props;

  if (!contestId || !roundId || !programId) {
    return (
      <div className="loyalty-card p-6 text-center text-sm text-[var(--color-text-muted)]">
        Contest block not configured (missing contestId, roundId, or programId).
      </div>
    );
  }

  const [contest, setContest] = useState<ContestData | null>(null);
  const [contestLoading, setContestLoading] = useState(true);

  // Fetch contest metadata from the active-contests list
  useEffect(() => {
    if (!loyaltyData) return;

    let cancelled = false;
    setContestLoading(true);

    apiGet<ContestData[]>(`/customer/loyalty/contests?programId=${programId}`)
      .then((contests) => {
        if (!cancelled) {
          const found = contests.find((c) => c.id === contestId) ?? null;
          setContest(found);
        }
      })
      .catch(() => {
        if (!cancelled) setContest(null);
      })
      .finally(() => {
        if (!cancelled) setContestLoading(false);
      });

    return () => { cancelled = true; };
  }, [loyaltyData, contestId, programId]);

  // Loading skeleton
  if (loyaltyLoading || contestLoading) {
    return (
      <div className="loyalty-card p-6 animate-pulse">
        <div className="h-6 w-48 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="h-4 w-64 bg-[var(--color-surface-border)] rounded mb-6" />
        <div className="h-12 w-full bg-[var(--color-surface-border)] rounded" />
      </div>
    );
  }

  // Not logged in
  if (!loyaltyData) {
    return <LoginCta contestType={contest?.contestType} />;
  }

  // Contest not found or not active
  if (!contest) {
    return (
      <div className="loyalty-card p-8 text-center">
        <p className="text-[var(--color-text-muted)] text-sm">
          This contest is not currently available.
        </p>
      </div>
    );
  }

  // Adaptive rendering by contest type
  if (contest.contestType === 'instant_win') {
    return <InstantWinView contest={contest} roundId={roundId} />;
  }

  if (contest.contestType === 'draw') {
    return <DrawView contest={contest} roundId={roundId} />;
  }

  if (contest.contestType === 'leaderboard') {
    return (
      <LeaderboardView
        contest={contest}
        roundId={roundId}
        maxEntries={maxLeaderboardEntries}
      />
    );
  }

  // Fallback
  return (
    <div className="loyalty-card p-8 text-center">
      <p className="text-[var(--color-text-muted)] text-sm">Unsupported contest type.</p>
    </div>
  );
}
